import { LockOutlined } from "@ant-design/icons";
import { Form } from "antd";
import { isMaskedValue } from "common/Utility";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import SchemaFieldTitle from "../SchemaFieldTitle";
import Tooltip from "../Tooltip";

const PhoneNumberInput = ({ value, onChange, onBlur, disabled }) => {
    const [phoneValue, setPhoneValue] = useState();

    useEffect(() => {
        // Ensuring that value is converted to E.164 format
        const phoneNumber = value ? parsePhoneNumber(value, "US") : "";
        setPhoneValue(phoneNumber ? phoneNumber.number : value);
    }, [value]);

    const onPhoneValueChange = (newValue) => {
        setPhoneValue(newValue);
        onChange?.(newValue);
    };

    return (
        <PhoneInput
            className="rounded-md border border-gray-300 px-1 [&>input]:p-1"
            placeholder="Enter phone number"
            value={phoneValue}
            onChange={onPhoneValueChange}
            onBlur={onBlur}
            disabled={disabled}
        />
    );
};

PhoneNumberInput.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    disabled: PropTypes.bool,
};

function FormPhoneField({ fieldErrors, fieldId, name, description, max, min, isPII, isRequired, disabled = false, ...field }) {
    const fieldValue = Form.useWatch([fieldId]);
    const isMasked = isMaskedValue(fieldValue);
    const isDisabled = disabled || (field.isPII && isMasked);

    return (
        <Form.Item
            key={fieldId}
            name={[fieldId]}
            label={
                <div style={{ display: "flex", alignItems: "center" }}>
                    <SchemaFieldTitle name={name} tags={field.classifiers} orientation="horizontal" tagSummary={false} />
                    {field.isPII && (
                        <Tooltip title="Masked data due to PII restriction">
                            <LockOutlined style={{ marginLeft: 8, color: "#1890ff" }} />
                        </Tooltip>
                    )}
                </div>
            }
            tooltip={description}
            validateFirst={true}
            validateTrigger={["onChange", "onBlur"]}
            rules={[
                {
                    required: isRequired,
                    message: `Value is required`,
                },
                {
                    min: min,
                    message: `Value should be minimum ${min} characters long`,
                },
                {
                    max: max,
                    message: `Value should be maximum ${max} characters long`,
                },
                {
                    validator(rule, value) {
                        if (!isPII && value && value.trim()) {
                            try {
                                const phoneNumber = parsePhoneNumber(value);
                                if (phoneNumber && (phoneNumber.isPossible() || phoneNumber.isValid())) {
                                    return Promise.resolve();
                                } else {
                                    return Promise.reject(`Invalid value: ${value}`);
                                }
                            } catch {
                                return Promise.reject(`Invalid value: ${value}`);
                            }
                        }
                        return Promise.resolve();
                    },
                },
            ]}
            help={fieldErrors?.help === "Invalid value" ? `Invalid value: ${fieldValue}` : fieldErrors?.help}
            validateStatus={fieldErrors?.validateStatus}
        >
            <PhoneNumberInput disabled={isDisabled} placeholder={isMasked ? "Masked PII - Use 'Reveal' to edit" : undefined} />
        </Form.Item>
    );
}

FormPhoneField.propTypes = {
    fieldErrors: PropTypes.object,
    fieldId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    max: PropTypes.number,
    min: PropTypes.number,
    isRequired: PropTypes.bool,
    disabled: PropTypes.bool,
    isPII: PropTypes.bool,
};

export default FormPhoneField;
